import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://www.swellbreaks.com';

/** Sitewide branded social-sharing image (1200x630 JPG on the Lovable CDN). */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/__l5e/assets-v1/4108aa95-b5d4-43a1-b08a-c2e486ecc46e/og-image.jpg`;
export const DEFAULT_OG_IMAGE_ALT = 'Swell Breaks — find the surf destination that fits you';

interface SeoProps {
  title: string;
  description: string;
  /** Path only, e.g. "/trip-planner" or "/destinations/uluwatu". */
  path: string;
  /** When true, emits <meta name="robots" content="noindex, follow"> for private routes. */
  noIndex?: boolean;
  /** Optional JSON-LD schema objects. Each becomes a single <script type="application/ld+json"> block. */
  jsonLd?: Record<string, unknown>[];
  /** Optional route-specific absolute image URL. Falls back to the sitewide Swell Breaks image. */
  image?: string;
  /** Alt text for the route-specific image. Ignored when `image` is not set. */
  imageAlt?: string;
}

/**
 * Normalises a raw slug the same way scripts/generate-sitemap.ts does, so
 * canonical URLs on destination pages match the sitemap and always resolve
 * to a single canonical form regardless of the dirty DB slug used in the URL.
 */
export function normaliseSlug(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function Seo({ title, description, path, noIndex = false, jsonLd, image, imageAlt }: SeoProps) {
  const url = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const robots = noIndex ? 'noindex, follow' : 'index, follow';
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ogImageAlt = image ? (imageAlt || title) : DEFAULT_OG_IMAGE_ALT;
  const isDefaultImage = ogImage === DEFAULT_OG_IMAGE;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      {isDefaultImage && <meta property="og:image:width" content="1200" />}
      {isDefaultImage && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
